import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import {
  CalAmBulk,
  getInitFormData,
  getNoSlip,
  applySlip,
  CalAm,
} from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo, ValueChangedEvent } from "@shared/type/CommonType"; // 공통 Interface
import SnResizeBar from "@shared/components/SnResizeBar";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { SnButton } from "@shared/components/SnButton";
import { dateToString } from "@shared/utils/DateUtils";
import SN_PRD_MRE_REG_001_CHEM_POP_001 from "@menu/XTC/SN_PRD_MRE_REG_001_CHEM/SN_PRD_MRE_REG_001_CHEM_POP_001";
import { showResizablePopupModal } from "@widgets/CommonModal";
import { RowNode } from "ag-grid-community/dist/types/src/entities/rowNode";
interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

const SN_PRD_MRE_REG_001_CHEM = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const { userInfo } = global(); // Login user 정보
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
    grid3: React.createRef(),
  });
  const searchRef = useRef<any>(null);
  const isUpdatingRef = useRef(false);
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });

  const [btnStChange, setbtnStChangeText] = useState<string>("발행");
  const [selectedDocu, setSelectedDocu] = useState<string>("");

  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();

        setFormControlData(rtn.formControlData);

        setFormData({
          ...rtn.formData,
          cd_module: Menuprops.cd_module,
        });

        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  const onSearchPanelValueChanged = (event: ValueChangedEvent) => {
    const { colId, newValue } = event;
    setFormData((prev: any) => ({
      ...prev,
      [colId]: newValue,
    }));
  };

  useEffect(() => {
    if (!formData?.dt) return;

    let isMounted = true;

    const fetchWeek = async () => {
      const params = {
        id: "sn_prd_mre_reg_001_chem_03",
        params: {
          cd_firm: userInfo.cd_firm,
          dt: formData.dt,
        },
      };

      try {
        const weekData: any = await fnGetDataSet(params);
        if (!isMounted) return;

        const row = weekData?.[0]?.[0] || {};

        setFormData((prev: any) => ({
          ...prev,
          week: row.week || "",
          dt_from: row.dt_from || "",
          dt_to: row.dt_to || "",
        }));
      } catch (error) {}
    };

    fetchWeek();

    return () => {
      isMounted = false;
    };
  }, [formData?.dt, userInfo.cd_firm]);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    const modifiedRows3 = gridRefs.current["grid3"].current.getChange();
    if (modifiedRows3.modifiedRowcount > 0) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        await onSave();
      }
    }
    try {
      const params = {
        id: "sn_prd_mre_reg_001_chem_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();

      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: data[2] || [],
      });
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onInsert = async () => {
    try {
      const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        await showDialog("alter", "수정된 내용이 있습니다.");
        return;
      }

      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());

      const newRow = {
        cd_firm: userInfo.cd_firm,
        cd_biz: searchInfo.cd_biz,
        no_plan: "",
        cd_dept: userInfo.cd_dept,
        cd_emp: userInfo.cd_emp,
        nm_emp: userInfo.nm_emp,
        cd_gp: userInfo.cd_gp,
        nm_gp: userInfo.nm_gp,
        dt_plan: today,
        st_plan: "F",
      };
      const addedRow = gridRefs.current["grid1"].current?.addRow(newRow);
    } catch (error: any) {
      console.error("Error in inserting row and focusing:", error);
    }
  };

  const onAddRow = () => {
    try {
      return gridRefs.current["grid2"].current?.addRow();
    } catch (error: any) {}
  };

  const onBeforeAddRowL = () => {
    try {
      const maxNoLine =
        gridRefs.current["grid2"].current.getMaxValue("no_line");
      return {
        cd_firm: userInfo.cd_firm,
        no_line: maxNoLine + 1,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onAddRowD = () => {
    try {
      return gridRefs.current["grid3"].current?.addRow();
    } catch (error: any) {}
  };

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    gridRefs.current["grid3"].current.deleteAllRows();
    gridRefs.current["grid2"].current.deleteAllRows();
    gridRefs.current["grid1"].current.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();
      return;
    }

    return await onSave();
  };

  const onDeleteRow = async () => {
    const grid1 = gridRefs.current["grid1"].current;

    const focusedRowNodeH = grid1.focusedRowNode();

    const grid3 = gridRefs.current["grid3"].current;
    const grid2 = gridRefs.current["grid2"].current;
    if (!grid3 || !grid2) return;

    const selectedRows = grid3.getSelectedRows();
    if (!selectedRows || selectedRows.length < 1) return;

    for (const row of selectedRows) {
      const deletedCdItem = row.cd_item;

      const params = {
        id: "sn_prd_mre_reg_001_chem_02",
        params: {
          cd_firm: userInfo.cd_firm,
          cd_item: deletedCdItem,
        },
      };

      const bomData: any = await fnGetDataSet(params);
      const cdMatlList = (bomData.flat() || []).map((b: any) => b.cd_matl);

      const nodesToRemove: RowNode[] = [];
      grid2.forEachNode((node: RowNode) => {
        if (cdMatlList.includes(node.data.cd_item)) {
          node.setSelected(true);
        }
        grid2.deleteSelectedRows();
      });
    }

    grid3.deleteSelectedRows();
    let sum = 0;

    grid2.forEachFilteredNode((node: RowNode) => {
      const value = Number(node.data?.qt_plan || 0);
      sum += value;
    });

    if (focusedRowNodeH) {
      focusedRowNodeH.setDataValue("qt_total", sum, "noEvent");
    }
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      const requiredValuesCheck1 =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheck1) {
        return;
      }
      gridRefs.current["grid2"].current?.stopEditing();
      const requiredValuesCheck2 =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheck2) {
        return;
      }
      gridRefs.current["grid3"].current?.stopEditing();
      const requiredValuesCheck3 =
        gridRefs.current["grid3"].current.requiredValuesCheck();
      if (!requiredValuesCheck3) {
        return;
      }

      const H = gridRefs.current["grid1"].current.getChange();
      const L = gridRefs.current["grid2"].current.getChange();
      const D = gridRefs.current["grid3"].current.getChange();

      if (
        H.modifiedRowcount === 0 &&
        L.modifiedRowcount === 0 &&
        D.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      const noSlip = await applySlip({
        cd_column: "no_plan",
        cd_module: Menuprops.cd_module,
        no_line: 17,
        cd_prefix: "",
        dt: focusedRowNodeH.data.dt_plan,
        girdrefs: [
          gridRefs.current["grid1"],
          gridRefs.current["grid2"],
          gridRefs.current["grid3"],
        ],
        inserts: [H, L, D],
        cd_menu: Menuprops.cd_menu,
      });

      const SaveData = {
        dtitemplanh_chem: H,
        dtitemplan_chem: L,
        dtitemplan_target_chem: D,
      };
      await fnSave("SN_PRD_MRE_REG_001_CHEM_SAVE_01", SaveData);
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };
  //#endregion

  //#region  Event Method
  const onFocusedRowChangedH = (event: any) => {
    if (!event) {
      return;
    }
    const no_plan = event.no_plan;
    setSelectedDocu(no_plan);

    if (event.st_plan === "F") {
      setbtnStChangeText("발행");
    } else {
      setbtnStChangeText("발행취소");
    }
  };

  const onCellValueChanged = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowNode = event.rowNode;
    if (colId === "st_plan") {
      if (rowNode.data.st_plan === "F") {
        setbtnStChangeText("발행");
      } else {
        setbtnStChangeText("발행취소");
      }
    }
  };

  const onCellValueChangedL = async (event: any) => {
    if (!event) return;
    const grid1 = gridRefs.current["grid1"].current;
    const grid2 = gridRefs.current["grid2"].current;

    const focusedRowNodeH = grid1.focusedRowNode();
    const colId = event.colId;

    if (colId === "qt_plan") {
      updateQtTotalFromGrid2();
    }
  };

  const onGrid1BtnsClick = async (id: string) => {
    if (id == "btnApply") {
      const salIsureqhChanges =
        gridRefs.current["grid1"].current.getChange().insert;
      if (salIsureqhChanges.length === 0) {
        await showDialog("alter", "신규행을 추가하시기 바랍니다.");
        return;
      }

      const selectedFormData = searchRef.current.getFormdata();
      const selectedData = {
        cd_biz: selectedFormData.cd_biz,
        dt_from: selectedFormData.dt_from,
        dt_to: selectedFormData.dt_to,
      };

      const component = (
        <SN_PRD_MRE_REG_001_CHEM_POP_001 selectedData={selectedData} />
      );
      const result = await showResizablePopupModal({
        title: "의뢰적용",
        children: component,
        validation: true,
      });
      if (!result || result === "close") return;

      const headerResult = result[0];
      const lineResult: any[] = result[1];
      const today = dateToString(new Date());
      const cdItems = [...new Set(lineResult.map((x: any) => x.cd_item))];
      const params = {
        id: "sn_prd_mre_reg_001_chem_02",
        params: {
          cd_firm: userInfo.cd_firm,
          cd_item: cdItems.join("|"),
        },
      };

      const bomData: any = await fnGetDataSet(params);
      const flatBomData = bomData.flat();

      const bomMap: Record<string, any[]> = {};

      flatBomData.forEach((bom: any) => {
        const cdItem = bom.cd_item;

        if (!cdItem) return;

        if (!bomMap[cdItem]) bomMap[cdItem] = [];
        bomMap[cdItem].push(bom);
      });

      const matlMap: Record<string, number> = {};

      lineResult.forEach((sol: any) => {
        const bomList = bomMap[sol.cd_item] || [];
        const qtApp = sol.qt_app || 0;

        bomList.forEach((bom: any) => {
          const key = bom.cd_matl;
          const qty = (bom.qt_need || 0) * qtApp;

          if (!matlMap[key]) matlMap[key] = 0;

          matlMap[key] += qty;
        });
      });

      Object.entries(matlMap).forEach(([cd_matl, qt]) => {
        const node = onAddRow();
        let maxNoLine =
          gridRefs.current["grid2"].current.getMaxValue("no_line") || 0;
        if (!node) return;
        const bomInfo = flatBomData.find((b: any) => b.cd_matl === cd_matl);
        node.setDataValue("cd_firm", userInfo.cd_firm, "noEvent");
        node.setDataValue("no_line", maxNoLine + 1, "noEvent");
        node.setDataValue("cd_item", cd_matl, "noEvent");
        node.setDataValue("qt_req", qt, "noEvent");
        node.setDataValue("qt_plan", qt, "noEvent");
        node.setDataValue("dt_req", today, "noEvent");
        if (bomInfo) {
          node.setDataValue("nm_item", bomInfo.nm_item, "noEvent");
          node.setDataValue("dc_item_spec", bomInfo.dc_item_spec, "noEvent");
          node.setDataValue("dc_item_unit", bomInfo.dc_item_unit, "noEvent");
        }
      });
      lineResult.map((result: any) => {
        if (headerResult[0]) {
          const addedNode = onAddRowD();
          if (addedNode) {
            addedNode.setDataValue("cd_firm", userInfo.cd_firm, "noEvent");
            addedNode.setDataValue(
              "no_so",
              result.no_so ? result.no_so : result.no_woreq,
              "noEvent"
            );

            addedNode.setDataValue(
              "no_line_so",
              result.no_line_so ? result.no_line_so : result.no_line,
              "noEvent"
            );
            addedNode.setDataValue("cd_item", result.cd_item, "noEvent");
            addedNode.setDataValue("nm_item", result.nm_item, "noEvent");
            addedNode.setDataValue(
              "dc_item_spec",
              result.dc_item_spec,
              "noEvent"
            );
            addedNode.setDataValue(
              "dc_item_unit",
              result.dc_item_unit,
              "noEvent"
            );
            addedNode.setDataValue("dt_dlv", result.dt_dlv, "noEvent");
            addedNode.setDataValue("qt_so", result.qt_app, "noEvent");
            addedNode.setDataValue("cd_wh", result.cd_wh, "noEvent");
            addedNode.setDataValue("nm_wh", result.nm_wh, "noEvent");
          }
        }
      });
      updateQtTotalFromGrid2();
    }
    const selectedRows = gridRefs.current["grid1"].current.focusedRowNode();
    if (id == "btnStChange") {
      if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) {
        return;
      }

      if (selectedRows.data?.st_plan === "F") {
        const conFirm = await showDialog("confirm", "발행 하시겠습니까?");
        if (conFirm) {
          const focusedNodeH =
            gridRefs.current["grid1"].current.focusedRowNode();
          const displayedNodes: any[] = [];
          gridRefs.current["grid2"].current.forEachFilteredNode(
            (node: RowNode) => {
              displayedNodes.push(node);
            }
          );
          const items = displayedNodes.map((node) => node.data);

          const payload = {
            header: {
              cd_firm: userInfo?.cd_firm,
              cd_dept: userInfo?.cd_dept,
              cd_emp: userInfo?.cd_emp,
              cd_gp: userInfo?.cd_gp,
              noPrd: focusedNodeH.data.no_plan,
              userId: userInfo?.cd_user,
              dt_plan: focusedNodeH.data.dt_plan,
              cd_biz: focusedNodeH.data.cd_biz,
            },
            items: items,
          };

          const ok = await fnSave("SN_PRD_MRE_REG_001_CHEM_APPROVE", payload);
          if (!ok) return false;
          selectedRows.setDataValue("st_plan", "R");
          gridRefs.current["grid1"].current.resetUpdate();
          gridRefs.current["grid2"].current.resetUpdate();
          gridRefs.current["grid3"].current.resetUpdate();
        } else {
          return;
        }
        setbtnStChangeText("발행취소");
      } else if (selectedRows.data?.st_plan === "R") {
        const conFirm = await showDialog("confirm", "발행 취소하시겠습니까?");
        if (conFirm) {
          const focusedNodeH =
            gridRefs.current["grid1"].current.focusedRowNode();
          const payload = {
            cd_firm: userInfo?.cd_firm,
            items: [focusedNodeH.data],
          };

          const ok = await fnSave("SN_PRD_MRE_REG_001_CHEM_CANCEL", payload);
          if (!ok) return false;
          selectedRows.setDataValue("st_plan", "F");
          gridRefs.current["grid1"].current.resetUpdate();
          gridRefs.current["grid2"].current.resetUpdate();
          gridRefs.current["grid3"].current.resetUpdate();
        } else {
          return;
        }
        setbtnStChangeText("발행");
      }
    }
  };

  const onGrid3BtnsClick = async (id: string) => {
    if (id == "deleteRow") {
      onDeleteRow();
    }
  };

  const updateQtTotalFromGrid2 = () => {
    const grid1 = gridRefs.current["grid1"].current;
    const grid2 = gridRefs.current["grid2"].current;

    const focusedRowNode = grid1.focusedRowNode();
    if (!focusedRowNode) return;

    let sum = 0;

    grid2.forEachFilteredNode((node: RowNode) => {
      sum += Number(node.data?.qt_plan ?? 0);
    });

    focusedRowNode.setDataValue("qt_total", sum);
  };

  //#endregion

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged: onFocusedRowChangedH,
          onCellValueChanged: onCellValueChanged,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
          onCellValueChanged: onCellValueChangedL,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {
          onBtnDeleteClick: onGrid3BtnsClick,
        },
      },
    }),
    [columns, dataSources]
  );

  //#region  UI
  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          onValueChanged={onSearchPanelValueChanged}
        />
      </div>

      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "생산계획" }}
            events={grids.grid1.events}
            filter={{
              refs: [gridRefs.current["grid2"], gridRefs.current["grid3"]],
              columns: ["no_plan"],
            }}
            header={
              <div className="flex justify-end">
                <SnButton
                  text={"의뢰적용"}
                  onClick={() => {
                    onGrid1BtnsClick("btnApply");
                  }}
                />
                <SnButton
                  text={btnStChange}
                  onClick={() => {
                    onGrid1BtnsClick("btnStChange");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <div className="flex w-full h-full min-w-0 space-x-1.5">
            <SnResizeBar direction={"width"} id="panel3" orgSize="60%">
              <SnGrid
                ref={gridRefs.current["grid2"]}
                columnConfig={grids.grid2.columns}
                rowData={grids.grid2.dataSource}
                etcProps={{ rowSelection: "single", title: "계획정보" }}
                events={grids.grid2.events}
              />
            </SnResizeBar>
            <div className="flex-1 min-w-0">
              <SnGrid
                ref={gridRefs.current["grid3"]}
                columnConfig={grids.grid3.columns}
                rowData={grids.grid3.dataSource}
                etcProps={{ rowSelection: "single", title: "수주정보" }}
                events={grids.grid3.events}
              />
            </div>
          </div>
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
  //#endregion
});
export default SN_PRD_MRE_REG_001_CHEM;
